import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowRight, ImageIcon, Loader2, Package, Upload } from 'lucide-react';
import { TaskyButton, TaskyCard, TaskyInput, TaskyAlert, TaskyConfirmDialog } from '../tasky';
import { ProductSelectionModal, ProductSelectionData } from '../image-generation';
import knowledgeApiService from '../../services/api/knowledge/knowledgeApiService';
import songAdService, { SongAdProject, UpdateSongAdProjectParams } from '../../services/api/songAdService';
import { ProductImage } from '../../types/knowledge';
import SongAdSettingsForm, {
  isValidTargetSeconds,
  joinWordsInput,
  splitWordsInput,
  SongAdSettingsValue,
} from './SongAdSettingsForm';
import SongAdImageKeysField, { SongAdImageKeyItem } from './SongAdImageKeysField';
import { useSetAdmanPageContext } from '../adman/useAdmanPageContext';

/**
 * ステップ①: 情報登録（v5 e）。
 *
 * これまで「作成設定を変更」モーダル（TaskyDialog + SongAdSettingsForm）でしか触れなかった
 * 作成設定を、専用タブとして常設する。商品選択・画像（v5 a・b）・音楽ジャンル（v5 c）・
 * タイトル（v5 d、リネーム可）もここに統合する。保存は PATCH /song-ads/{id}。
 *
 * advisor-notes (3): 保存には**影響グループ別のガード**を入れる（旧「条件を変更」モーダルには
 * ガードが一切無かった）。
 * - 歌詞・楽曲に効くフィールド（CM構成/ジャンル/曲調/ボーカル/テンポ/尺/イントロ/自由記述/必須・禁止語）を
 *   変更したとき、曲またはシーンが既に生成済みなら確認する
 * - 映像に効くフィールド（比率/商品/画像）を変更したとき、シーンが既に生成済みなら確認する
 * - タイトル・CTAは無害なので確認不要
 * サーバー側は非破壊（自動削除しない）。作り直しは既存の再生成ボタンに委ねる。
 *
 * v6 F2（o）: CTAロゴの明示指定UIをここに追加する。`advanced_settings.logo_image_key`
 * （backend B1の契約。自組織の商品画像/アップロード画像のS3キー）で保存する。ロゴ選択も
 * タイトル・CTAと同じく無害グループ（見た目だけ・歌詞や映像の作り直しを要求しない）扱いとし、
 * 確認ダイアログの対象には含めない。
 */

interface InfoStepProps {
  project: SongAdProject;
  onSave: (params: UpdateSongAdProjectParams) => Promise<boolean>;
}

const toSettingsValue = (project: SongAdProject): SongAdSettingsValue => ({
  cmType: project.cm_type,
  genreTag: project.genre_tag ?? null,
  moodTag: (project.style_tags[0] as SongAdSettingsValue['moodTag']) ?? 'bright',
  vocalGender: project.vocal_gender,
  tempo: project.tempo,
  // v6 F7: 廃止済みの尺（15秒）は選択肢に無いのでプレースホルダ(30)にしておき、
  // retiredTargetSeconds に実値を残す（セレクタは専用の無効化タイルで表示する）
  targetSeconds: isValidTargetSeconds(project.target_seconds) ? project.target_seconds : 30,
  retiredTargetSeconds: isValidTargetSeconds(project.target_seconds) ? null : project.target_seconds,
  aspectRatio: project.aspect_ratio,
  intro: project.intro,
  freeText: project.style_free_text ?? '',
  requiredWordsText: joinWordsInput(project.advanced_settings?.required_words ?? []),
  bannedWordsText: joinWordsInput(project.advanced_settings?.banned_words ?? []),
  cta: project.advanced_settings?.cta ?? '',
});

const sortedKey = (keys: string[]): string => JSON.stringify([...keys].sort());

/**
 * 商品画像から `category === 'brand_logo'` のものだけをロゴ候補として抽出する（v6 F2）。
 * `ProductSelectionModal.tsx` の `logoImages` フィルタ（L199-201）と同じ発想。
 */
const extractBrandLogoImages = (images?: ProductImage[] | null): SongAdImageKeyItem[] =>
  (images ?? [])
    .filter((img) => img.category === 'brand_logo')
    .map((img) => ({
      key: img.file_path,
      url: img.url,
      label: img.title || img.file_name,
      source: 'product' as const,
    }));

const InfoStep: React.FC<InfoStepProps> = ({ project, onSave }) => {
  // 各フィールドはマウント時（＝このタブを開いた時点）のprojectから初期化するローカルドラフト。
  // 他ステップの下書き（歌詞・シーン設定）と同じく、保存操作のときだけサーバーへ送る。
  const [title, setTitle] = useState(project.title);
  const [settings, setSettings] = useState<SongAdSettingsValue>(() => toSettingsValue(project));
  const [productId, setProductId] = useState<number | null>(project.product_id);
  // 広告自動化マンに「今この商品を見ている」と伝える（F20-a）。既存ロジックには触れない。
  useSetAdmanPageContext(productId ? { product_ids: [productId] } : null);
  const [productName, setProductName] = useState<string | undefined>(undefined);
  const [showProductSelectionModal, setShowProductSelectionModal] = useState(false);
  const [imageItems, setImageItems] = useState<SongAdImageKeyItem[]>([]);

  // CTAロゴの明示指定（v6 F2/o）。null＝自動（backendが brand_logo → product_image_keys[0] の順で解決）
  const [logoImageKey, setLogoImageKey] = useState<string | null>(project.advanced_settings?.logo_image_key ?? null);
  const [brandLogoCandidates, setBrandLogoCandidates] = useState<SongAdImageKeyItem[]>([]);
  const [uploadedLogoCandidates, setUploadedLogoCandidates] = useState<SongAdImageKeyItem[]>([]);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [logoUploadError, setLogoUploadError] = useState<string | null>(null);
  const logoFileInputRef = useRef<HTMLInputElement>(null);

  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);

  // 保存ガードの判定基準（マウント時点の値。影響グループの差分検出に使う）
  const initialRef = useRef({
    settings: toSettingsValue(project),
    productId: project.product_id,
    productImageKeysSorted: sortedKey(project.product_image_keys),
  });
  const hasSong = !!project.song_url;
  const hasScenes = (project.scenes ?? []).length > 0;

  // 既存の product_id / product_image_keys から、表示できる範囲で初期状態を復元する。
  // アップロードのみで追加された画像はプレビューURLを復元できないため、汎用アイコン表示にする
  // （キー自体は失わない。意図的な簡略化 — 実行メモ参照）。
  useEffect(() => {
    let cancelled = false;

    const restoreImagesWithoutProduct = () => {
      if (project.product_image_keys.length === 0) return;
      setImageItems(
        project.product_image_keys.map((key) => ({
          key,
          url: null,
          label: 'アップロード画像',
          source: 'upload' as const,
        }))
      );
    };

    const load = async () => {
      if (!project.product_id) {
        restoreImagesWithoutProduct();
        return;
      }
      try {
        const product = await knowledgeApiService.getProduct(project.product_id);
        if (cancelled) return;
        setProductName(product.name);
        const items: SongAdImageKeyItem[] = project.product_image_keys.map((key) => {
          const match = product.product_images?.find((img) => img.file_path === key);
          return match
            ? { key, url: match.url, label: match.title || match.file_name, source: 'product' as const }
            : { key, url: null, label: 'アップロード画像', source: 'upload' as const };
        });
        setImageItems(items);
        setBrandLogoCandidates(extractBrandLogoImages(product.product_images));
      } catch {
        // 商品取得に失敗してもキー自体は失わない（プレビュー無しで表示）
        if (!cancelled) restoreImagesWithoutProduct();
      }
    };

    load();
    return () => {
      cancelled = true;
    };
    // マウント時（＝このタブを開いた時点）にのみ初期化する
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const productImageKeys = useMemo(() => imageItems.map((i) => i.key), [imageItems]);

  const handleSettingsChange = (patch: Partial<SongAdSettingsValue>) => {
    setSettings((prev) => ({ ...prev, ...patch }));
  };

  /**
   * 商品選択モーダルの確定時。商品由来の画像だけを新しい選択で置き換え、アップロード画像は残す。
   * v6 F2: ロゴ候補（category=brand_logo）も新しい商品のものに差し替える
   * （`data.product.product_images` は一覧取得時点で既に持っているのでAPI再取得は不要）。
   */
  const handleProductSelected = (data: ProductSelectionData) => {
    setProductId(data.product.id);
    setProductName(data.product.name);
    setShowProductSelectionModal(false);
    setImageItems((prev) => {
      const uploadsOnly = prev.filter((i) => i.source !== 'product');
      const productItems: SongAdImageKeyItem[] = data.selectedImages.map((img) => ({
        key: img.file_path,
        url: img.url,
        label: img.title || img.file_name,
        source: 'product',
      }));
      return [...productItems, ...uploadsOnly];
    });
    setBrandLogoCandidates(extractBrandLogoImages(data.product.product_images));
  };

  /** ロゴ候補の一覧（ロゴ画像＋現在選択中の画像＋アップロードしたロゴ。重複キーは先勝ち） */
  const logoCandidates = useMemo(() => {
    const merged = [...brandLogoCandidates, ...imageItems, ...uploadedLogoCandidates];
    const seen = new Set<string>();
    return merged.filter((item) => {
      if (seen.has(item.key)) return false;
      seen.add(item.key);
      return true;
    });
  }, [brandLogoCandidates, imageItems, uploadedLogoCandidates]);

  const savedLogoImageKey = project.advanced_settings?.logo_image_key ?? null;
  const selectedLogoCandidate = logoCandidates.find((c) => c.key === logoImageKey) ?? null;
  // 未変更（保存済みの値のまま）なら backend 解決済みの project.cta.logo_url を見せる。
  // 変更後は手元にある候補のプレビューURLがあればそれを、無ければ保存するまで分からないので出さない
  const resolvedLogoPreviewUrl =
    selectedLogoCandidate?.url ?? (logoImageKey === savedLogoImageKey ? project.cta?.logo_url ?? null : null);

  const handleLogoFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoUploadError(null);

    if (!file.type.startsWith('image/')) {
      setLogoUploadError(`${file.name} は画像ファイルではないためアップロードできません`);
      if (logoFileInputRef.current) logoFileInputRef.current.value = '';
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setLogoUploadError(`${file.name} は10MBを超えているためアップロードできません`);
      if (logoFileInputRef.current) logoFileInputRef.current.value = '';
      return;
    }

    setIsUploadingLogo(true);
    try {
      const { s3Key } = await songAdService.uploadImage(file);
      setUploadedLogoCandidates((prev) => [
        ...prev,
        { key: s3Key, url: URL.createObjectURL(file), label: file.name, source: 'upload' },
      ]);
      setLogoImageKey(s3Key);
    } catch (err) {
      setLogoUploadError(`${file.name} のアップロードに失敗しました（${songAdService.extractErrorMessage(err)}）`);
    } finally {
      setIsUploadingLogo(false);
      if (logoFileInputRef.current) logoFileInputRef.current.value = '';
    }
  };

  const buildParams = (): UpdateSongAdProjectParams => ({
    title: title.trim() || undefined,
    product_id: productId,
    product_image_keys: productImageKeys,
    aspect_ratio: settings.aspectRatio,
    genre_tag: settings.genreTag,
    style_tags: [settings.moodTag],
    style_free_text: settings.freeText.trim() || undefined,
    vocal_gender: settings.vocalGender,
    tempo: settings.tempo,
    // v6 F7: 廃止済みの尺（15秒）のまま未変更なら target_seconds を送らない。
    // 15を送るとbackendのvalidationで422になる。送らなければ部分更新で現在値が保持される
    ...(settings.retiredTargetSeconds === null ? { target_seconds: settings.targetSeconds } : {}),
    cm_type: settings.cmType,
    intro: settings.intro,
    advanced_settings: {
      required_words: splitWordsInput(settings.requiredWordsText),
      banned_words: splitWordsInput(settings.bannedWordsText),
      cta: settings.cta.trim() || null,
      logo_image_key: logoImageKey,
    },
  });

  /**
   * 保存対象の変更が「歌詞・楽曲に効くグループ」または「映像に効くグループ」に触れているかを判定する
   * （advisor-notes (3) の表どおり。title/cta は無害グループなのでここでは見ない）。
   */
  const needsConfirm = (): boolean => {
    const initial = initialRef.current;

    const lyricsOrSongChanged =
      settings.cmType !== initial.settings.cmType ||
      settings.moodTag !== initial.settings.moodTag ||
      settings.genreTag !== initial.settings.genreTag ||
      settings.vocalGender !== initial.settings.vocalGender ||
      settings.tempo !== initial.settings.tempo ||
      settings.targetSeconds !== initial.settings.targetSeconds ||
      // v6 F7: 廃止済みの尺(15秒)から30/45/60への切り替えは、targetSeconds のプレースホルダ値が
      // 偶然一致する場合（30→30）でも実質的な変更なので retiredTargetSeconds の変化も見る
      settings.retiredTargetSeconds !== initial.settings.retiredTargetSeconds ||
      settings.intro !== initial.settings.intro ||
      settings.freeText.trim() !== initial.settings.freeText.trim() ||
      settings.requiredWordsText !== initial.settings.requiredWordsText ||
      settings.bannedWordsText !== initial.settings.bannedWordsText;

    const visualChanged =
      settings.aspectRatio !== initial.settings.aspectRatio ||
      productId !== initial.productId ||
      sortedKey(productImageKeys) !== initial.productImageKeysSorted;

    return (lyricsOrSongChanged && (hasSong || hasScenes)) || (visualChanged && hasScenes);
  };

  const runSave = async () => {
    setIsSaving(true);
    setError(null);
    try {
      const ok = await onSave(buildParams());
      if (!ok) setError('保存に失敗しました');
    } catch (e) {
      setError(songAdService.extractErrorMessage(e));
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveClick = () => {
    if (needsConfirm()) {
      setShowConfirm(true);
      return;
    }
    runSave();
  };

  const handleConfirmedSave = () => {
    setShowConfirm(false);
    runSave();
  };

  return (
    <div className="space-y-5">
      {error && <TaskyAlert tone="error">{error}</TaskyAlert>}

      <TaskyCard variant="warm" padding="md">
        <h3 className="text-sm font-semibold text-dark mb-4">情報登録</h3>

        <div className="space-y-5">
          {/* タイトル（任意。空のまま保存すると自動命名のまま） */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">タイトル</label>
            <TaskyInput
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={100}
              disabled={isSaving}
              placeholder="未入力の場合は自動で名前が付きます"
            />
          </div>

          {/* 商品選択（任意） */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">商品選択（任意）</label>
            <div className="flex items-center gap-2 flex-wrap">
              <TaskyButton
                type="button"
                variant="outline"
                size="sm"
                disabled={isSaving}
                onClick={() => setShowProductSelectionModal(true)}
              >
                <Package className="w-3.5 h-3.5" />
                {productName ? '商品を変更' : '商品を選択する'}
              </TaskyButton>
              {productName && (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-50 text-green-700 rounded-full text-xs">
                  {productName}
                </span>
              )}
            </div>
          </div>

          {/* 画像（商品選択で選んだ画像＋アップロード。任意・最大3枚） */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">画像（任意・最大3枚）</label>
            <SongAdImageKeysField items={imageItems} onChange={setImageItems} disabled={isSaving} />
          </div>

          {/* CTAロゴ（任意。v6 F2/o）。未指定なら商品のロゴ画像→選択画像の1枚目の順で自動的に使われる */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">CTAロゴ（任意）</label>
            <p className="text-[11px] text-gray-400 mb-2">
              未指定なら商品のロゴ画像→選択画像の1枚目の順で使います。
            </p>
            <div className="flex items-start gap-3">
              <div className="w-16 h-16 rounded-lg border border-warm-border bg-warm-surface flex items-center justify-center overflow-hidden shrink-0">
                {resolvedLogoPreviewUrl ? (
                  <img src={resolvedLogoPreviewUrl} alt="選択中のロゴ" className="w-full h-full object-contain p-1" />
                ) : (
                  <ImageIcon className="w-6 h-6 text-gray-300" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    disabled={isSaving}
                    onClick={() => setLogoImageKey(null)}
                    title="自動（商品のロゴ画像→選択画像の1枚目の順で自動選択）"
                    className={`w-14 h-14 rounded-lg border-2 flex items-center justify-center bg-white shrink-0 transition-colors disabled:opacity-50 ${
                      logoImageKey === null
                        ? 'border-primary'
                        : 'border-warm-border hover:border-primary/50'
                    }`}
                  >
                    <span className="text-[10px] text-gray-500 px-1 text-center leading-tight">自動</span>
                  </button>
                  {logoCandidates.map((item) => (
                    <button
                      key={item.key}
                      type="button"
                      disabled={isSaving}
                      onClick={() => setLogoImageKey(item.key)}
                      title={item.label}
                      className={`w-14 h-14 rounded-lg overflow-hidden border-2 flex items-center justify-center bg-white shrink-0 transition-colors disabled:opacity-50 ${
                        logoImageKey === item.key
                          ? 'border-primary'
                          : 'border-warm-border hover:border-primary/50'
                      }`}
                    >
                      {item.url ? (
                        <img
                          src={item.url}
                          alt={`ロゴ候補: ${item.label}`}
                          className="w-full h-full object-contain p-1"
                        />
                      ) : (
                        <ImageIcon className="w-5 h-5 text-gray-400" />
                      )}
                    </button>
                  ))}
                </div>
                <div className="mt-2">
                  <button
                    type="button"
                    disabled={isSaving || isUploadingLogo}
                    onClick={() => logoFileInputRef.current?.click()}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs border border-warm-border rounded-tasky-sm text-gray-600 hover:border-primary hover:text-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isUploadingLogo ? (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    ) : (
                      <Upload className="w-3 h-3" />
                    )}
                    ロゴをアップロード
                  </button>
                  <input
                    ref={logoFileInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleLogoFileChange}
                  />
                </div>
                {logoUploadError && <p className="text-[11px] text-destructive mt-1">{logoUploadError}</p>}
              </div>
            </div>
          </div>

          <SongAdSettingsForm
            value={settings}
            onChange={handleSettingsChange}
            disabled={isSaving}
            productNamePlaceholder={productName}
          />
        </div>

        <div className="flex justify-end pt-4 mt-4 border-t border-warm-border">
          <TaskyButton onClick={handleSaveClick} isLoading={isSaving}>
            <ArrowRight className="w-4 h-4" />
            保存して次へ
          </TaskyButton>
        </div>
      </TaskyCard>

      {showProductSelectionModal && (
        <ProductSelectionModal
          isOpen={showProductSelectionModal}
          onClose={() => setShowProductSelectionModal(false)}
          onConfirm={handleProductSelected}
        />
      )}

      <TaskyConfirmDialog
        open={showConfirm}
        onClose={() => setShowConfirm(false)}
        onConfirm={handleConfirmedSave}
        tone="warning"
        title="情報を変更しますか？"
        description="ここでの変更は、既に生成済みの歌詞・楽曲・シーンには自動的に反映されません（必要に応じて後で作り直してください）。保存しますか？"
        confirmText="変更を保存する"
      />
    </div>
  );
};

export default InfoStep;
